import { useCallback, useState } from "react";

import { INTERACTIVE_ELEMENTS_SELECTOR } from "./constants";

export interface UseCellModeArgs {
    itemCount: number;
    isDisabledAt?: (index: number) => boolean;
}

export type CellMode = "navigation" | "interaction";

/**
 * Check if an element has interactive children
 */
export const hasInteractiveElements = (element: HTMLElement | null): boolean => {
    return element ? element.querySelectorAll(INTERACTIVE_ELEMENTS_SELECTOR).length > 0 : false;
};

/**
 * Hook to manage navigation vs interaction mode for list items
 */
const useCellMode = ({ itemCount, isDisabledAt = () => false }: UseCellModeArgs) => {
    const [cellMode, setCellMode] = useState<CellMode>("navigation");
    const [interactiveCellIndex, setInteractiveCellIndex] = useState<number>(-1);

    const enterCellInteractionMode = useCallback(
        (index: number) => {
            if (index >= 0 && index < itemCount && !isDisabledAt(index)) {
                setCellMode("interaction");
                setInteractiveCellIndex(index);
            }
        },
        [itemCount, isDisabledAt],
    );

    const exitCellInteractionMode = useCallback(() => {
        setCellMode("navigation");
        setInteractiveCellIndex(-1);
    }, []);

    return {
        cellMode,
        interactiveCellIndex,
        enterCellInteractionMode,
        exitCellInteractionMode,
    };
};

export { useCellMode };
